// File: src/components/host/modals/HostAgendaPreviewModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { LuX } from 'react-icons/lu';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function useIsMobile(breakpointPx = 520) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpointPx}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mq = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    const onChange = () => setIsMobile(mq.matches);

    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);

    onChange();

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, [breakpointPx]);

  return isMobile;
}

function pickFirst(...vals) {
  for (const v of vals) {
    const s = typeof v === 'string' ? v.trim() : v;
    if (s) return s;
  }
  return '';
}

function toIdString(v) {
  if (!v) return '';
  if (typeof v === 'object') {
    const maybe = v._id || v.id;
    if (maybe) return String(maybe).trim();
    return '';
  }
  return String(v).trim();
}

function to12Hour(timeStr) {
  const s = String(timeStr || '').trim();
  if (!s) return '';
  if (/\b(AM|PM)\b/i.test(s)) return s;

  if (/^\d{1,2}:\d{2}$/.test(s)) {
    const [hhRaw, mmRaw] = s.split(':').map(Number);
    if (!Number.isFinite(hhRaw) || !Number.isFinite(mmRaw)) return s;

    const hh = Math.max(0, Math.min(23, hhRaw));
    const mm = Math.max(0, Math.min(59, mmRaw));
    const hour12 = ((hh + 11) % 12) + 1;
    const ampm = hh >= 12 ? 'PM' : 'AM';
    return `${hour12}:${String(mm).padStart(2, '0')} ${ampm}`;
  }

  return s;
}

function formatTimeRange(start, end) {
  const s = to12Hour(start);
  const e = to12Hour(end);
  if (!s) return '';
  if (!e) return s;
  return `${s} – ${e}`;
}

function formatEventDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Agenda stored as flat fields on submission/event doc
 */
function extractAgendaItems(ev) {
  if (!ev) return [];

  const hasFlatAgenda =
    ev.welcomeStart ||
    ev.openingStart ||
    ev.icebreakerStart ||
    ev.primaryStart ||
    ev.transitionStart ||
    ev.secondaryStart ||
    ev.closingStart ||
    ev.icebreakerHowItWorks ||
    ev.primaryName ||
    ev.primaryDescription ||
    ev.secondaryName ||
    ev.secondaryDescription;

  if (!hasFlatAgenda) return [];

  const items = [
    {
      key: 'welcome',
      label: 'Welcome guests',
      time: formatTimeRange(ev.welcomeStart, ev.welcomeEnd),
      detail: 'Welcome guests as they arrive and make sure nobody is lost or awkward.',
    },
    {
      key: 'opening',
      label: 'Opening introduction',
      time: formatTimeRange(ev.openingStart, ev.openingEnd),
      detail: 'Start the event, set context, and explain the plan.',
    },
    {
      key: 'icebreaker',
      label: 'Icebreaker',
      time: formatTimeRange(ev.icebreakerStart, ev.icebreakerEnd),
      detail: pickFirst(ev.icebreakerHowItWorks, ev.icebreaker, ev.icebreakerName),
    },
    {
      key: 'primary',
      label: pickFirst(ev.primaryName, 'Primary activity'),
      time: formatTimeRange(ev.primaryStart, ev.primaryEnd),
      detail: pickFirst(ev.primaryDescription),
    },
    {
      key: 'transition',
      label: 'Transition',
      time: formatTimeRange(ev.transitionStart, ev.transitionEnd),
      detail: 'Move cleanly into the next phase.',
    },
    {
      key: 'secondary',
      label: pickFirst(ev.secondaryName, 'Secondary activity'),
      time: formatTimeRange(ev.secondaryStart, ev.secondaryEnd),
      detail: pickFirst(ev.secondaryDescription),
    },
    {
      key: 'closing',
      label: 'Closing',
      time: formatTimeRange(ev.closingStart, ev.closingEnd),
      detail: 'Wrap up the night and point people back to Tribe.',
    },
  ];

  return items.filter((x) => (x.time && x.time.trim()) || (x.detail && String(x.detail).trim()));
}

function normalizeResponseDoc(resData) {
  return resData?.submission || resData?.event || resData?.data || resData;
}

export default function HostAgendaPreviewModal({ open, event, onClose }) {
  const isMobile = useIsMobile(520);
  const closeBtnRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [fullEvent, setFullEvent] = useState(null);

  const eventId = useMemo(() => toIdString(event?._id), [event]);
  const approvedEventId = useMemo(() => toIdString(event?.approvedEventId), [event]);
  const sourceSubmissionId = useMemo(() => toIdString(event?.sourceSubmissionId), [event]);
  const explicitSubmissionId = useMemo(() => toIdString(event?.submissionId), [event]);

  const submissionIdGuess = useMemo(() => {
    if (sourceSubmissionId) return sourceSubmissionId;
    if (explicitSubmissionId) return explicitSubmissionId;
    if (approvedEventId) return eventId; // submission doc shape
    return '';
  }, [sourceSubmissionId, explicitSubmissionId, approvedEventId, eventId]);

  const liveEventIdGuess = useMemo(() => {
    if (approvedEventId) return approvedEventId;
    return eventId;
  }, [approvedEventId, eventId]);

  // ✅ Lock background scroll (robust) + Escape-to-close
  // ✅ Lock background scroll (robust) + Escape-to-close + notch/theme color
useEffect(() => {
  if (!open) return;

  const meta = document.querySelector('meta[name="theme-color"]');
  const prevTheme = meta?.content;

  // If another overlay already locked the page, don't fight it.
  const alreadyLocked =
    document.body.classList.contains('overlay-active') ||
    document.body.style.position === 'fixed';

  const didLock = !alreadyLocked;

  // Keep scroll position so it doesn't jump
  const scrollY = window.scrollY || window.pageYOffset || 0;

  // Set notch/status bar color
  if (meta) meta.content = '#999998';

  // Only lock if nothing else is already locking
  if (didLock) {
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.classList.add('overlay-active'); // uses your index.css rules
  }

  const t = setTimeout(() => closeBtnRef.current?.focus?.(), 0);

  function onKeyDown(e) {
    if (e.key === 'Escape') onClose?.();
  }
  window.addEventListener('keydown', onKeyDown);

  return () => {
    clearTimeout(t);
    window.removeEventListener('keydown', onKeyDown);

    // Restore notch/status bar color
    if (meta) meta.content = prevTheme || '#ffffff';

    // Only unlock if we were the ones who locked it
    if (didLock) {
      document.body.classList.remove('overlay-active');
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    }
  };
}, [open, onClose]);

  // Fetch agenda source (submission preferred)
  useEffect(() => {
    if (!open) return;

    let mounted = true;

    async function fetchAgenda() {
      setLoadErr('');
      setFullEvent(null);
      setLoading(true);

      const token =
        localStorage.getItem('accessToken') ||
        localStorage.getItem('authToken') ||
        '';

      const headers = {
        Authorization: `Bearer ${token}`,
        'X-Suppress-Global-Error': '1',
      };

      try {
        const directItems = extractAgendaItems(event || {});
        if (directItems.length > 0) {
          if (mounted) setFullEvent(event);
          return;
        }

        if (submissionIdGuess) {
          const res = await axios.get(`${API}/host/submissions/${submissionIdGuess}`, {
            headers,
            validateStatus: (s) => [200, 401, 403, 404].includes(s),
          });

          if (res.status === 200) {
            const found = normalizeResponseDoc(res.data);
            if (mounted && found) {
              setFullEvent(found);
              return;
            }
          }
        }

        if (liveEventIdGuess) {
          const res2 = await axios.get(`${API}/events/${liveEventIdGuess}`, {
            headers,
            validateStatus: (s) => [200, 401, 403, 404].includes(s),
          });

          if (res2.status === 200) {
            const live = normalizeResponseDoc(res2.data);

            if (mounted && live) {
              const liveItems = extractAgendaItems(live);
              if (liveItems.length > 0) {
                setFullEvent(live);
                return;
              }

              const src = toIdString(live?.sourceSubmissionId);
              if (src) {
                const res3 = await axios.get(`${API}/host/submissions/${src}`, {
                  headers,
                  validateStatus: (s) => [200, 401, 403, 404].includes(s),
                });

                if (res3.status === 200) {
                  const sub = normalizeResponseDoc(res3.data);
                  if (mounted && sub) {
                    setFullEvent(sub);
                    return;
                  }
                }
              }
            }
          }
        }

        if (!mounted) return;
        setLoadErr('No agenda was found for this event.');
      } catch (e) {
        if (!mounted) return;
        setLoadErr(e?.response?.data?.message || e?.message || 'No agenda was found for this event.');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchAgenda();
    return () => { mounted = false; };
  }, [open, submissionIdGuess, liveEventIdGuess, event]);

  const effective = fullEvent || event || {};
  const items = useMemo(() => extractAgendaItems(effective), [effective]);

  const meta = useMemo(() => {
    const d = formatEventDate(effective?.eventDate);
    const tr = formatTimeRange(
      effective?.startTime12 || effective?.startTime,
      effective?.endTime12 || effective?.endTime
    );
    return [d, tr].filter(Boolean).join(' · ');
  }, [effective]);

  if (!open) return null;

  return (
    <div
      style={S.backdrop}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      onTouchStart={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Event agenda"
        style={{
          ...S.sheet,
          ...(isMobile ? S.sheetMobile : S.sheetDesktop),
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {/* notch / grabber */}
        <div style={S.notchWrap} aria-hidden="true">
          <div style={S.notch} />
        </div>

        {/* Header */}
        <div style={S.header}>
          <div style={S.headerTitle}>Event Agenda</div>

          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={S.closeBtn}
          >
            <LuX size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={S.body}>
          {/* Event title/meta ABOVE agenda list */}
          <div style={S.eventBlock}>
            <div style={S.eventName}>{effective?.name || 'Your event'}</div>
            {meta ? <div style={S.eventMeta}>{meta}</div> : null}
          </div>

          {loading ? (
            <div style={S.stateBox}>Loading agenda…</div>
          ) : loadErr ? (
            <div style={S.errBox}>{loadErr}</div>
          ) : items.length === 0 ? (
            <div style={S.stateBox}>No agenda was found for this event.</div>
          ) : (
            <div style={S.list}>
              {items.map((it, idx) => (
                <div key={it.key} style={S.row}>
                  <div style={S.rowTop}>
                    <div style={S.stageNum} aria-hidden="true">{idx + 1}</div>
                    <div style={S.rowTitle}>{it.label}</div>
                    {it.time ? <div style={S.timePill}>{it.time}</div> : null}
                  </div>

                  {it.detail ? (
                    <div style={S.rowDetail}>{it.detail}</div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <button type="button" onClick={onClose} style={S.primaryBtn}>
            Done
          </button>
        </div>

        {/* safe area */}
        <div style={{ height: 'env(safe-area-inset-bottom)' }} aria-hidden="true" />

        <style>{`
          @keyframes slideUp { from { transform: translateY(16px); opacity:0 } to { transform: translateY(0); opacity:1 } }
          @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important } }
          button:focus-visible { outline: 0; box-shadow: 0 0 0 3px rgba(59,130,246,.45) }
        `}</style>
      </div>
    </div>
  );
}

/* ===============================
   STYLE TOKENS (match DateFilter vibe)
=============================== */
const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    padding: 8,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  sheet: {
    width: '100%',
    maxWidth: 520,
    background: '#ffffff',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: 'slideUp .25s ease-out',
  },

  sheetDesktop: { marginBottom: 8 },
  sheetMobile: { borderRadius: 12 },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 48,
    padding: '0 12px',
    borderBottom: '1px solid #eef0f2',
    gap: 10,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
  },

  closeBtn: {
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    width: 32,
    height: 32,
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    lineHeight: 0,
    boxSizing: 'border-box',
    color: '#0b0b0b',
    flexShrink: 0,
  },

  body: {
    maxHeight: 'min(60vh, 520px)',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: '10px 12px 12px',
  },

  eventBlock: {
    borderRadius: 10,
    padding: '10px 12px',
    marginBottom: 10,
        background: '#eeee',

  },
  eventName: {
    fontSize: 13,
    fontWeight: 900,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  eventMeta: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 700,
    color: '#000',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  stateBox: {
    border: '0px solid #eef0f2',
    background: '#fafafa',
    borderRadius: 10,
    padding: '10px 12px',
    fontSize: 13,
    fontWeight: 700,
    color: '#374151',
  },
  errBox: {
    border: '1px solid rgba(185, 28, 28, 0.18)',
    background: 'rgba(185, 28, 28, 0.08)',
    borderRadius: 10,
    padding: '10px 12px',
    fontSize: 13,
    fontWeight: 800,
    color: '#7f1d1d',
  },

  list: { display: 'flex', flexDirection: 'column', gap: 8 },

  row: {
    borderBottom: '1px solid #eef0f2',
    background: '#ffffff',
    padding: '16px 12px',
  },

  rowTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },

  // ✅ numbered stage marker
  stageNum: {
    width: 22,
    height: 22,
    borderRadius: 100,
    border: '1px solid #eef0f2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 900,
    color: '#0b0b0b',
    flexShrink: 0,
  },

  rowTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: '#0b0b0b',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  timePill: {
    marginLeft: 'auto',
    border: '1px solid #eef0f2',
    background: '#fafafa',
    borderRadius: 999,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 800,
    color: '#000',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },

  rowDetail: {
    marginTop: 6,
    fontSize: 12.5,
    fontWeight: 650,
    color: '#374151',
    lineHeight: 1.35,
    marginLeft:30
  },

  footer: {
    padding: 12,
    borderTop: '1px solid #eef0f2',
    display: 'flex',
    gap: 8,
  },

  primaryBtn: {
    flex: 1,
    background: '#0b0b0b',
    color: '#ffffff',
    border: '1px solid #0b0b0b',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },
};
